import { z } from "zod"

import {
  branchIdSchema,
  commandIdSchema,
  correlationIdSchema,
  entityIdSchema,
  identityIdSchema,
  legalEntityIdSchema,
  membershipIdSchema,
  tenantIdSchema,
} from "./identifiers.js"
import { safeEntitySummaryV1Schema } from "./command-result-v1.js"

const outboxContextV1Schema = z
  .object({
    legalEntityId: legalEntityIdSchema.nullable(),
    branchId: branchIdSchema.nullable(),
    entityType: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/).nullable(),
    entityId: entityIdSchema.nullable(),
  })
  .strict()

const outboxActorV1Schema = z
  .object({
    identityId: identityIdSchema,
    membershipId: membershipIdSchema,
  })
  .strict()

const outboxCommandV1Schema = z
  .object({
    commandId: commandIdSchema,
    correlationId: correlationIdSchema,
    idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
    commandType: z.string().regex(/^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+$/),
    commandVersion: z.number().int().positive().safe(),
  })
  .strict()

const outboxOutcomeV1Schema = z
  .object({
    result: z.enum(["accepted", "rejected"]),
    reasonCode: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    before: safeEntitySummaryV1Schema.nullable(),
    after: safeEntitySummaryV1Schema.nullable(),
  })
  .strict()

export const commandOutboxEventV1Schema = z
  .object({
    eventVersion: z.literal(1),
    eventId: z.string().regex(/^[a-f0-9]{64}$/),
    eventType: z.literal("impetusos.command.completed"),
    occurredAt: z.iso.datetime({ offset: true }),
    tenantId: tenantIdSchema,
    context: outboxContextV1Schema,
    actor: outboxActorV1Schema,
    command: outboxCommandV1Schema,
    outcome: outboxOutcomeV1Schema,
  })
  .strict()

export const customerPaymentProviderEventV1Schema = z
  .object({
    eventVersion: z.literal(1),
    eventId: z.string().regex(/^[a-f0-9]{64}$/),
    eventType: z.literal("impetusos.customer_payment.provider_event_processed"),
    occurredAt: z.iso.datetime({ offset: true }),
    tenantId: tenantIdSchema,
    actor: z.object({ kind: z.literal("provider_system") }).strict(),
    providerEvent: z.object({
      eventRef: z.string().regex(/^[a-f0-9]{16}$/),
      depositAttemptId: entityIdSchema.nullable(),
      state: z.enum(["processed", "ignored", "reconciliation_required"]),
      reasonCode: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    }).strict(),
  })
  .strict()

export const outboxEventV1Schema = z.discriminatedUnion("eventType", [
  commandOutboxEventV1Schema,
  customerPaymentProviderEventV1Schema,
])

export type OutboxEventV1 = z.infer<typeof outboxEventV1Schema>

export const outboxCursorV1Schema = z
  .object({
    occurredAt: z.iso.datetime({ offset: true }),
    eventId: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict()

export const outboxPageV1Schema = z
  .object({
    pageVersion: z.literal(1),
    tenantId: tenantIdSchema,
    events: z.array(outboxEventV1Schema).max(100),
    nextCursor: outboxCursorV1Schema.nullable(),
    hasMore: z.boolean(),
  })
  .strict()
  .superRefine((page, context) => {
    if (page.events.some((event) => event.tenantId !== page.tenantId)) {
      context.addIssue({ code: "custom", message: "Outbox page Tenant mismatch." })
    }
    if (page.hasMore && page.nextCursor === null) {
      context.addIssue({ code: "custom", message: "Outbox page cursor is required." })
    }
  })

export type OutboxCursorV1 = z.infer<typeof outboxCursorV1Schema>
export type OutboxPageV1 = z.infer<typeof outboxPageV1Schema>
